import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import { ResetIcon } from "@src/assets/icons/ResetIcon";
import Button from "../Button/Button";
import { ButtonContainer, Container, FilterButton, Form, HeaderLabel, InputWrapper, Main } from "./Header.styled";
import { ButtonVariant } from "../Button/Button.constants";
import { inputItems } from "./InputItems";
import { EVENTS_SEARCH_PLACEHOLDER } from "@src/constants/eventsSearch";
import { paths } from "@src/routes/paths";
import { Input } from "../Input/Input";
import { SelectYup } from "../Select/SelectYup";
import { DatePicker } from "../DatePicker/DatePicker";
import FiltersModal, { marketplaceOptions } from "../Modal/FiltersModal/FiltersModal";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { CompanyFilterMode, filtersEmptyValues, FiltersProps, FiltersValidation } from "./Header.constants";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { eventsActions } from "@src/store";
import { fetchEvents } from "@src/store/eventsSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { GitIcon } from "@src/assets/icons/GitIcon";
import PullEventsModal from "../Modal/PullEventsModal/PullEventsModal";
import { OptionType } from "@src/utils/reactSelectUtils";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { MultiSelectYup } from "@src/components/MultiSelect/MultiSelectYup";
import { CompanyMultiSelectYup } from "@src/components/MultiSelect/CompanyMultiSelectYup";
import { getInventoryDisplayModeFromSearchParams } from "@src/utils/inventoryDisplayMode";
import { isMarketplaceHiddenInUi, sanitizeMarketplaceForUi } from "@src/constants/marketplaceVisibility";
import { ApplyEventFiltersOptions, useEventFilters } from "@src/context/EventFiltersContext";
import { setCurrentPage as setVenueAliasesCurrentPage, setSearchQuery as setVenueAliasesSearchQuery } from "@src/store/venueAliasesSlice";
import ReactSelect from "react-select";
import { filtersConfig } from "@src/utils/reactSelectUtils";
import {
	resetFilters as resetAiConfidenceFilters,
	setMarketplaceId as setAiConfidenceMarketplaceId,
	setSearchTerm as setAiConfidenceSearchTerm,
	setSortBy as setAiConfidenceSortBy,
	setTriageState as setAiConfidenceTriageState,
} from "@src/store/aiConfidenceSlice";
import { AiConfidenceSortBy, AiConfidenceTriageState } from "@src/pages/AiConfidence/types";
import { getMarketplaceIdByName } from "@src/constants/marketplaceEventUrls";
import { Items } from "@src/components/Sidebar/Items";

interface EventCategoryDto {
	id: number;
	type: string;
	description: string;
	active: boolean;
}

interface CategoryOption extends OptionType<string> {
	isActive: boolean;
	type: string;
	description: string;
}

const parseCsvSearchParam = (value: string | null): Array<string> =>
	value?.split(",").map((x) => x.trim()).filter(Boolean) ?? [];

const getCompanyModeFromSearchParams = (searchParams: URLSearchParams): CompanyFilterMode => {
	const modeParam = searchParams.get("companyFilterMode");

	if (
		modeParam === CompanyFilterMode.ALL ||
		modeParam === CompanyFilterMode.INCLUDE ||
		modeParam === CompanyFilterMode.EXCLUDE
	) {
		return modeParam;
	}

	return CompanyFilterMode.ALL;
};

const getCompanySelectionFromSearchParams = (
	searchParams: URLSearchParams,
	allCompanies: Array<OptionType<string>>,
): { mode: CompanyFilterMode; selectedCompanyIds: Array<string> } => {
	const mode = getCompanyModeFromSearchParams(searchParams);
	const payloadIds = parseCsvSearchParam(searchParams.get("companyIds"));
	const allIds = allCompanies.map((c) => c.value);
	const allIdSet = new Set(allIds);

	if (mode === CompanyFilterMode.ALL) {
		return { mode, selectedCompanyIds: allIds };
	}

	if (mode === CompanyFilterMode.EXCLUDE) {
		if (!allIds.length) {
			return { mode, selectedCompanyIds: [] };
		}

		const excludedSet = new Set(payloadIds);
		return {
			mode,
			selectedCompanyIds: allIds.filter((id) => !excludedSet.has(id)),
		};
	}

	return {
		mode,
		selectedCompanyIds: payloadIds.filter((id) => (allIds.length ? allIdSet.has(id) : true)),
	};
};

const buildCompanyFilterQuery = (
	selectedCompanyIds: Array<string>,
	allCompanies: Array<OptionType<string>>,
	mode: CompanyFilterMode,
): { mode: CompanyFilterMode; companyIds: Array<string> } => {
	const allIds = allCompanies.map((c) => c.value);
	const allIdSet = new Set(allIds);
	const selected = Array.from(new Set(selectedCompanyIds)).filter((id) => allIdSet.has(id));

	if (!allIds.length) {
		return { mode: CompanyFilterMode.ALL, companyIds: [] };
	}

	if (mode === CompanyFilterMode.ALL) {
		return { mode: CompanyFilterMode.ALL, companyIds: [] };
	}

	if (mode === CompanyFilterMode.INCLUDE) {
		return { mode: CompanyFilterMode.INCLUDE, companyIds: selected };
	}

	const selectedSet = new Set(selected);
	const excluded = allIds.filter((id) => !selectedSet.has(id));

	if (!excluded.length) {
		return { mode: CompanyFilterMode.ALL, companyIds: [] };
	}

	return { mode: CompanyFilterMode.EXCLUDE, companyIds: excluded };
};

const clearDisabledEventFilters = (filters: FiltersProps): FiltersProps => ({
	...filters,
	companyId: "",
	companyIds: [],
	categoryId: [],
});

const MANUAL_EVENT_PULL_TOOLTIP_ID = "manual-event-pull-tooltip";

const Header = () => {
	const appDispatch = useAppDispatch();
	const [params, setParams] = useSearchParams();
	const { registerApplyCurrentEventFilters } = useEventFilters();
	const tableType = useAppSelector((state) => state.events.tableType);
	const defaultValues = useMemo(
		() => ({
			marketplace: sanitizeMarketplaceForUi(params.get("marketplace")),
			companyId: params.get("companyId") || "",
			companyIds: getCompanySelectionFromSearchParams(params, []).selectedCompanyIds,
			eventName: params.get("eventName") || "",
			eventHeadliner: params.get("eventHeadliner") || "",
			venue: params.get("venue") || "",
			startDate: params.get("startDate") || "",
			endDate: params.get("endDate") || "",
			search: params.get("search") || "",
			categoryId: parseCsvSearchParam(params.get("categoryId")),
		}),
		[params],
	);

	const location = useLocation();
	const previousPathRef = useRef(location.pathname);
	const [isPullEventsModalOpen, setPullEventsModalOpen] = useState(false);
	const isPoMapping = location.pathname.includes("/po-mapping");
	const isVenueAliases = location.pathname.includes("/venue-aliases");
	const isAiConfidence = location.pathname.includes("/ai-confidence");
	const pageLabel = useMemo(() => {
		const match = Items.find(({ navigateTo }) =>
			navigateTo === paths.home ? location.pathname === paths.home : !!navigateTo && location.pathname.startsWith(navigateTo),
		);

		return (match?.label ?? "Events").toUpperCase();
	}, [location.pathname]);
	const aiConfidenceMarketplaceId = useAppSelector((state) => state.aiConfidence.marketplaceId);
	const aiConfidenceTriageState = useAppSelector((state) => state.aiConfidence.triageState);
	const aiConfidenceSortBy = useAppSelector((state) => state.aiConfidence.sortBy);
	const aiConfidenceSearchTerm = useAppSelector((state) => state.aiConfidence.searchTerm);
	const isEventsPage = location.pathname === paths.home;
	const [companyOptions, setCompanyOptions] = useState<Array<OptionType<string>>>([]);
	const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
	const isUnmappedTab = tableType === "UNMAPPED_EVENTS";
	const isSleptTab = tableType === "SLEPT_EVENTS";
	const isCompanyMultiSelectTab = tableType === "UNMAPPED_EVENTS" || tableType === "MAPPED_EVENTS";
	const isCompanyFilterEnabled = !isSleptTab;
	const isCategoryFilterEnabled = true;
	const [companyFilterMode, setCompanyFilterMode] = useState<CompanyFilterMode>(
		getCompanyModeFromSearchParams(params),
	);
	const [categoryOptions, setCategoryOptions] = useState<Array<CategoryOption>>([]);
	const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
	const venueAliasesSearchQuery = useAppSelector((state) => state.venueAliases.searchQuery);

	const groupedCategoryOptions = useMemo(() => {
		const active = categoryOptions.filter(o => o.isActive);
		const inactive = categoryOptions.filter(o => !o.isActive);

		return [
			{
				label: "ACTIVE",
				options: active,
			},
			{
				label: "INACTIVE",
				options: inactive,
			},
		];
	}, [categoryOptions]);

	const { control, handleSubmit, reset, setValue, getValues } = useForm<FiltersProps>({
		mode: "onChange",
		resolver: yupResolver(FiltersValidation),
		defaultValues: { ...defaultValues },
	});

	useEffect(() => {
		const cameFromAnotherRoute = previousPathRef.current !== location.pathname;
		const cameFromVenueAliases = previousPathRef.current.includes("/venue-aliases");

		if (isVenueAliases && cameFromAnotherRoute) {
			setParams(new URLSearchParams(), { replace: true });
			appDispatch(setVenueAliasesSearchQuery(""));
			appDispatch(setVenueAliasesCurrentPage(1));
			reset({ ...getValues(), search: "" });
		} else if (!isVenueAliases && cameFromAnotherRoute && cameFromVenueAliases) {
			reset({ ...getValues(), search: "" });
		}

		previousPathRef.current = location.pathname;
	}, [appDispatch, getValues, isVenueAliases, location.pathname, reset, setParams]);

	useEffect(() => {
		const marketplaceParam = params.get("marketplace");
		if (!marketplaceParam || !isMarketplaceHiddenInUi(marketplaceParam)) return;

		const nextParams = new URLSearchParams(params);
		nextParams.delete("marketplace");
		setParams(nextParams, { replace: true });
		setValue("marketplace", "");
	}, [params, setParams, setValue]);

	useEffect(() => {
		if (!isCompanyMultiSelectTab) return;
		if (params.has("companyFilterMode")) return;

		const nextParams = new URLSearchParams(params);
		nextParams.set("companyFilterMode", CompanyFilterMode.ALL);
		setParams(nextParams, { replace: true });
	}, [isCompanyMultiSelectTab, params, setParams]);

	useEffect(() => {
		let isMounted = true;

		const fetchCategories = async () => {
			setIsCategoriesLoading(true);
			try {
				const { data } = await axios.get(`${getBackendUrl()}/event-categories?includeInactive=true`, {
					headers: getAuthHeaders(),
				});

				if (!isMounted) return;

				const categories: Array<EventCategoryDto> = Array.isArray(data?.categories) ? data.categories : [];

				const sorted = [...categories].sort((a, b) => {
					const at = (a.type || "").toLowerCase();
					const bt = (b.type || "").toLowerCase();
					if (at !== bt) return at.localeCompare(bt);

					const ad = (a.description || "").toLowerCase();
					const bd = (b.description || "").toLowerCase();
					return ad.localeCompare(bd);
				});

				const options: Array<CategoryOption> = sorted.map((c) => ({
					value: String(c.id),
					label: `${(c.description || "").trim()} (${(c.type || "").trim()})${c.active ? "" : " (Inactive)"}`,
					isActive: c.active,
					type: c.type,
					description: c.description,
				}));

				setCategoryOptions(options);
			} catch (_error) {
				if (isMounted) setCategoryOptions([]);
			} finally {
				if (isMounted) setIsCategoriesLoading(false);
			}
		};

		void fetchCategories();

		return () => {
			isMounted = false;
		};
	}, []);


	useEffect(() => {
		if (!isCompanyFilterEnabled) return;

		let isMounted = true;

		const fetchCompanies = async () => {
			setIsCompaniesLoading(true);
			try {
				const { data } = await axios.get(`${getBackendUrl()}/companies`, {
					headers: getAuthHeaders(),
				});
				if (!isMounted) return;
				const options = Array.isArray(data)
					? data
						.map((company: { companyId: number; companyName: string }) => ({
							value: String(company.companyId),
							label: company.companyName?.trim() || `Company ${company.companyId}`,
						}))
						.sort((a: OptionType<string>, b: OptionType<string>) => a.label.localeCompare(b.label))
					: [];
				setCompanyOptions(options);
			} catch (error) {
				console.error("Failed to load companies", error);
			} finally {
				if (isMounted) setIsCompaniesLoading(false);
			}
		};

		void fetchCompanies();

		return () => {
			isMounted = false;
		};
	}, [isCompanyFilterEnabled]);

	useEffect(() => {
		const nextCategoryIds = isCategoryFilterEnabled ? parseCsvSearchParam(params.get("categoryId")) : [];
		setValue("categoryId", nextCategoryIds);
	}, [isCategoryFilterEnabled, params, setValue]);

	useEffect(() => {
		if (!isSleptTab) return;

		const nextParams = new URLSearchParams(params);
		let hasDisabledFilterParams = false;

		["companyId", "companyIds", "companyFilterMode"].forEach((key) => {
			if (!nextParams.has(key)) return;
			nextParams.delete(key);
			hasDisabledFilterParams = true;
		});

		setCompanyFilterMode(CompanyFilterMode.ALL);
		setValue("companyId", "");
		setValue("companyIds", []);

		const currentValues = getValues();
		if (currentValues.companyId || currentValues.companyIds?.length) {
			appDispatch(eventsActions.setFilters(clearDisabledEventFilters(currentValues)));
		}

		if (hasDisabledFilterParams) {
			setParams(nextParams, { replace: true });
		}
	}, [appDispatch, getValues, isSleptTab, params, setParams, setValue]);

	useEffect(() => {
		if (!isCompanyFilterEnabled) {
			setCompanyFilterMode(CompanyFilterMode.ALL);
			setValue("companyId", "");
			setValue("companyIds", []);
			return;
		}

		if (!isCompanyMultiSelectTab) {
			setCompanyFilterMode(CompanyFilterMode.ALL);
			setValue("companyId", params.get("companyId") || "");
			setValue("companyIds", []);
			return;
		}

		const { mode, selectedCompanyIds } = getCompanySelectionFromSearchParams(params, companyOptions);
		setCompanyFilterMode(mode);
		setValue("companyId", "");
		setValue("companyIds", selectedCompanyIds);
	}, [companyOptions, isCompanyFilterEnabled, isCompanyMultiSelectTab, params, setValue]);

	// Watch the startDate to set minimum date for endDate
	const startDate = useWatch({
		control,
		name: "startDate",
	});

	const endDate = useWatch({
		control,
		name: "endDate",
	});
	const marketplace = useWatch({
		control,
		name: "marketplace",
	});
	const companyIds = useWatch({
		control,
		name: "companyIds",
	});

	const today = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);

	const isSearchDisabled = !!(
		isUnmappedTab && startDate && endDate && new Date(startDate) < today && new Date(endDate) < today
	);
	const isMarketplaceMissing = !marketplace;
	const selectedCompanyIdsCount = (Array.isArray(companyIds) ? companyIds : []).filter(Boolean).length;
	const isCompanySelectionInvalid =
		isCompanyMultiSelectTab &&
		companyOptions.length > 0 &&
		companyFilterMode !== CompanyFilterMode.ALL &&
		selectedCompanyIdsCount === 0;
	const shouldShowDateTooltip = !isMarketplaceMissing && isSearchDisabled;
	const isSearchButtonDisabled = isMarketplaceMissing || isSearchDisabled || isCompanySelectionInvalid;

	// Convert startDate string to Date object for minDate prop
	const minEndDate = useMemo(() => {
		if (!startDate) return undefined;
		return new Date(startDate);
	}, [startDate]);

	const applyFilters = useCallback((data: FiltersProps, options?: ApplyEventFiltersOptions) => {
		if (isAiConfidence) {
			appDispatch(setAiConfidenceSearchTerm(data.search?.trim() ?? ""));
			return;
		}

		if (isVenueAliases) {
			const nextParams = new URLSearchParams();
			const aliasTab = params.get("tab");
			const nextSearchValue = data.search?.trim() ?? "";

			if (aliasTab) {
				nextParams.set("tab", aliasTab);
			}

			if (nextSearchValue) {
				nextParams.set("search", nextSearchValue);
			}

			appDispatch(setVenueAliasesSearchQuery(nextSearchValue));
			appDispatch(setVenueAliasesCurrentPage(1));
			setParams(nextParams, options?.replace ? { replace: true } : undefined);
			reset({ ...getValues(), search: nextSearchValue });
			return;
		}

		const companyNormalizedData: FiltersProps = isCompanyFilterEnabled ? data : { ...data, companyId: "", companyIds: [] };
		const categoryNormalizedData: FiltersProps = isCategoryFilterEnabled
			? companyNormalizedData
			: clearDisabledEventFilters(companyNormalizedData);
		const normalizedData: FiltersProps = {
			...categoryNormalizedData,
			search: categoryNormalizedData.search?.trim() ?? "",
		};
		const newParams = new URLSearchParams();
		const prevParamsString = params.toString();
		const hasSearchValue = Boolean(normalizedData.search);

			// Preserve includeInactiveEvents (default false unless search is used)
			const includeInactiveParam = params.get("includeInactiveEvents");
			const includeInactiveEvents =
				hasSearchValue
					? true
					: options?.includeInactiveEvents !== undefined
						? options.includeInactiveEvents
						: includeInactiveParam !== null
							? includeInactiveParam === "true"
							: false;
		newParams.set("includeInactiveEvents", includeInactiveEvents ? "true" : "false");

		// Preserve inventoryDisplayMode
		const inventoryDisplayModeValue = options?.inventoryDisplayMode ?? getInventoryDisplayModeFromSearchParams(params);
		newParams.set("inventoryDisplayMode", inventoryDisplayModeValue);

		// Preserve sort parameters
		const sortByValue = params.get("sortBy");
		if (sortByValue !== null) {
			newParams.set("sortBy", sortByValue);
		}
		const sortDirectionValue = params.get("sortDirection");
		if (sortDirectionValue !== null) {
			newParams.set("sortDirection", sortDirectionValue);
		}

		// Preserve hasNotes
		const hasNotesValue = params.get("hasNotes");
		if (hasNotesValue === "true") {
			newParams.set("hasNotes", "true");
		}

		if (isCompanyMultiSelectTab) {
			const selectedCompanyIds = (normalizedData.companyIds ?? []).filter((id): id is string => Boolean(id));
			if (
				companyFilterMode !== CompanyFilterMode.ALL &&
				companyOptions.length > 0 &&
				selectedCompanyIds.length === 0
			) {
				return;
			}
			const companyFilter = buildCompanyFilterQuery(selectedCompanyIds, companyOptions, companyFilterMode);
			newParams.set("companyFilterMode", companyFilter.mode);
			if (companyFilter.companyIds.length) {
				newParams.set("companyIds", companyFilter.companyIds.join(","));
			}
		}

		Object.entries(normalizedData).forEach(([key, value]) => {
			if (!isCompanyFilterEnabled && (key === "companyId" || key === "companyIds")) return;
			if (!isCategoryFilterEnabled && key === "categoryId") return;

			if (key === "companyIds") return;
			if (key === "companyId" && isCompanyMultiSelectTab) return;

			if (key === "categoryId") {
				const arr = Array.isArray(value) ? value : [];
				if (arr.length) newParams.set("categoryId", arr.join(","));
				return;
			}

			if (value) {
				newParams.set(key, String(value));
			}
		});
		const newParamsString = newParams.toString();

		appDispatch(eventsActions.setFilters(normalizedData));
		setParams(newParams, options?.replace ? { replace: true } : undefined);
		reset(normalizedData);

		if (newParamsString === prevParamsString) {
			const requestId = Date.now();
			appDispatch(
				fetchEvents({ inventoryDisplayMode: inventoryDisplayModeValue, requestId, includeInactiveEvents }),
			);
		}
	}, [
		appDispatch,
		companyFilterMode,
		companyOptions,
		getValues,
		isAiConfidence,
		isCategoryFilterEnabled,
		isCompanyFilterEnabled,
		isCompanyMultiSelectTab,
		isVenueAliases,
		params,
		reset,
		setParams,
	]);

	const applyCurrentFilters = useCallback((options?: ApplyEventFiltersOptions) => {
		applyFilters(getValues(), options);
	}, [applyFilters, getValues]);

	useEffect(() => {
		registerApplyCurrentEventFilters(applyCurrentFilters);

		return () => {
			registerApplyCurrentEventFilters(null);
		};
	}, [applyCurrentFilters, registerApplyCurrentEventFilters]);

	const onSubmit: SubmitHandler<FiltersProps> = (data) => {
		applyFilters(data);
	};

	const handleReset = () => {
		if (isAiConfidence) {
			appDispatch(resetAiConfidenceFilters());
			reset({ ...getValues(), search: "" });
			return;
		}

		if (isVenueAliases) {
			const nextParams = new URLSearchParams();
			const aliasTab = params.get("tab");

			if (aliasTab) {
				nextParams.set("tab", aliasTab);
			}

			appDispatch(setVenueAliasesSearchQuery(""));
			appDispatch(setVenueAliasesCurrentPage(1));
			setParams(nextParams, { replace: true });
			reset({ ...getValues(), search: "" });
			return;
		}

		const newParams = new URLSearchParams();

			// Preserve includeInactiveEvents (default false)
			const includeInactiveParam = params.get("includeInactiveEvents");
			const includeInactiveEvents = includeInactiveParam !== null ? includeInactiveParam === "true" : false;
		newParams.set("includeInactiveEvents", includeInactiveEvents ? "true" : "false");

		// Preserve inventoryDisplayMode
		const inventoryDisplayModeValue = getInventoryDisplayModeFromSearchParams(params);
		newParams.set("inventoryDisplayMode", inventoryDisplayModeValue);

		// Preserve sort parameters
		const sortByValue = params.get("sortBy");
		if (sortByValue !== null) {
			newParams.set("sortBy", sortByValue);
		}
		const sortDirectionValue = params.get("sortDirection");
		if (sortDirectionValue !== null) {
			newParams.set("sortDirection", sortDirectionValue);
		}
		if (isCompanyMultiSelectTab) {
			newParams.set("companyFilterMode", CompanyFilterMode.ALL);
			setCompanyFilterMode(CompanyFilterMode.ALL);
		}

		const resetValues: FiltersProps =
			isCompanyMultiSelectTab
				? {
					...filtersEmptyValues,
					companyIds: companyOptions.map((company) => company.value),
				}
				: filtersEmptyValues;

		setParams(newParams);
		appDispatch(eventsActions.setFilters(resetValues));
		reset(resetValues);
	};
	const filtersCounter = Object.entries(getValues()).filter(([key, value]) => {
		if (!isCompanyFilterEnabled && (key === "companyId" || key === "companyIds")) return false;
		if (!isCategoryFilterEnabled && key === "categoryId") return false;
		if (isCompanyMultiSelectTab && key === "companyId") return false;
		if (!isCompanyMultiSelectTab && key === "companyIds") return false;
		if (key === "companyIds") return companyFilterMode !== CompanyFilterMode.ALL;
		if (Array.isArray(value)) return value.length > 0;
		return !!value;
	}).length;

	const openPullEventsModal = () => setPullEventsModalOpen(true);
	const closePullEventsModal = () => setPullEventsModalOpen(false);

	const selectOptionsByName: Record<string, Array<OptionType<string>>> = useMemo(
		() => ({
			marketplace: marketplaceOptions,
			companyId: companyOptions,
		}),
		[companyOptions],
	);

	const visibleInputItems = useMemo(
		() => (isVenueAliases || isAiConfidence ? inputItems.filter(({ name }) => name === "search") : inputItems),
		[isAiConfidence, isVenueAliases],
	);

	const mappedInputs = visibleInputItems
		.map(({ name, placeholder, type, showType }) => (
			<InputWrapper $showType={showType} key={name} $name={name} $width={isAiConfidence ? "220px" : undefined}>
				<div style={{ width: "100%" }}>
					{showType === "input" && (
						<Input
							wrapperStyle={{ width: "100%" }}
							type={type}
							placeholder={name === "search" && isEventsPage ? EVENTS_SEARCH_PLACEHOLDER : placeholder}
							name={name}
							control={control}
							disableXButton={name === "search"}
						/>
					)}

					{name === "companyId" && (isCompanyMultiSelectTab || isSleptTab) && (
						<CompanyMultiSelectYup
							name="companyIds"
							control={control}
							placeholder={placeholder}
							options={companyOptions}
							isDisabled={!isCompanyFilterEnabled}
							isLoading={isCompaniesLoading}
							maxSelected={20}
							mode={companyFilterMode}
							onModeChange={setCompanyFilterMode}
						/>
					)}

					{showType === "multiselect" && name === "categoryId" && (
						<MultiSelectYup
							name="categoryId"
							control={control}
							placeholder="Categories"
							options={groupedCategoryOptions}
							isDisabled={!isCategoryFilterEnabled}
							isLoading={isCategoriesLoading}
						/>
					)}

					{showType === "select" && !(name === "companyId" && (isCompanyMultiSelectTab || isSleptTab)) && (
						<SelectYup
							name={name}
							placeholder={placeholder}
							options={selectOptionsByName[name] || []}
							control={control}
							isDisabled={name === "companyId" && !isCompanyFilterEnabled}
							isLoading={name === "companyId" ? isCompaniesLoading : false}
						/>
					)}
					{showType === "date" && (
						<DatePicker
							wrapperStyle={{ minWidth: "100px" }}
							placeholder={placeholder}
							name={name}
							control={control}
							minDate={
								name === "endDate"
									? isUnmappedTab
										? new Date()
										: minEndDate
									: name === "startDate" && isUnmappedTab
										? new Date()
										: undefined
							}
						/>
					)}
				</div>
			</InputWrapper>
		));

	useEffect(() => {
		if (!isAiConfidence) return;

		if (getValues("search") !== aiConfidenceSearchTerm) {
			setValue("search", aiConfidenceSearchTerm, { shouldDirty: false });
		}
	}, [aiConfidenceSearchTerm, getValues, isAiConfidence, setValue]);

	useEffect(() => {
		if (!isVenueAliases) return;

		const nextSearchValue = params.get("search") ?? "";
		if (getValues("search") !== nextSearchValue) {
			setValue("search", nextSearchValue, { shouldDirty: false });
		}
		if (venueAliasesSearchQuery !== nextSearchValue) {
			appDispatch(setVenueAliasesSearchQuery(nextSearchValue));
		}
	}, [appDispatch, getValues, isVenueAliases, params, setValue, venueAliasesSearchQuery]);

	const filtersProps = {
		control,
		reset,
		setValue,
		handleReset,
		filtersCounter,
	};

	if (isAiConfidence) {
		const selectStyles = filtersConfig("none", "m");
		const marketplaceSelectOptions: Array<OptionType> = [
			{ value: "", label: "All marketplaces" },
			...marketplaceOptions
				.map(({ value, label }) => ({ value: String(getMarketplaceIdByName(value) ?? ""), label }))
				.filter((option) => option.value !== ""),
		];
		const triageStateOptions: Array<OptionType> = [
			{ value: "open", label: "Open" },
			{ value: "acknowledged", label: "Acknowledged" },
		];
		const sortOptions: Array<OptionType> = [
			{ value: "urgency", label: "Urgency (worst first)" },
			{ value: "checkedOn", label: "Last checked" },
			{ value: "marketplace", label: "Marketplace" },
		];
		const pick = (options: Array<OptionType>, value: string) => options.find((option) => option.value === value) ?? null;

		return (
			<Main>
				<Container>
					<HeaderLabel>{pageLabel}</HeaderLabel>
					<Form onSubmit={handleSubmit(onSubmit)}>
						{mappedInputs}
						<InputWrapper $showType="select" $name="marketplace">
							<div style={{ width: "100%" }}>
								<ReactSelect<OptionType, false>
									options={marketplaceSelectOptions}
									value={pick(marketplaceSelectOptions, aiConfidenceMarketplaceId == null ? "" : String(aiConfidenceMarketplaceId))}
									onChange={(option) => appDispatch(setAiConfidenceMarketplaceId(option?.value ? Number(option.value) : null))}
									styles={selectStyles}
									components={{ IndicatorSeparator: null }}
									isSearchable={false}
								/>
							</div>
						</InputWrapper>
						<InputWrapper $showType="select" $name="triageState" $width="150px">
							<div style={{ width: "100%" }}>
								<ReactSelect<OptionType, false>
									options={triageStateOptions}
									value={pick(triageStateOptions, aiConfidenceTriageState)}
									onChange={(option) => appDispatch(setAiConfidenceTriageState((option?.value ?? "open") as AiConfidenceTriageState))}
									styles={selectStyles}
									components={{ IndicatorSeparator: null }}
									isSearchable={false}
								/>
							</div>
						</InputWrapper>
						<InputWrapper $showType="select" $name="sortBy" $width="205px">
							<div style={{ width: "100%" }}>
								<ReactSelect<OptionType, false>
									options={sortOptions}
									value={pick(sortOptions, aiConfidenceSortBy)}
									onChange={(option) => appDispatch(setAiConfidenceSortBy((option?.value ?? "urgency") as AiConfidenceSortBy))}
									styles={selectStyles}
									components={{ IndicatorSeparator: null }}
									isSearchable={false}
								/>
							</div>
						</InputWrapper>
						<ButtonContainer>
							<Button type="button" onClick={handleReset} variant={ButtonVariant.Quaternary}>
								<FilterButton>
									<ResetIcon />
								</FilterButton>
							</Button>
							<Button style={{ width: "100px" }} type="submit">
								Search
							</Button>
						</ButtonContainer>
					</Form>
				</Container>
			</Main>
		);
	}

	if (isVenueAliases) {
		return (
			<Main>
				<Container>
					<HeaderLabel>{pageLabel}</HeaderLabel>
					<Form onSubmit={handleSubmit(onSubmit)}>
						{mappedInputs}
						<ButtonContainer>
							<Button type="button" onClick={handleReset} variant={ButtonVariant.Quaternary}>
								<FilterButton>
									<ResetIcon />
								</FilterButton>
							</Button>
							<Button style={{ width: "100px" }} type="submit">
								Search
							</Button>
						</ButtonContainer>
					</Form>
				</Container>
			</Main>
		);
	}

	return (
		<>
			<PullEventsModal isOpen={isPullEventsModalOpen} onClose={closePullEventsModal} />
			<Main>
				<Container>
					<HeaderLabel>{pageLabel}</HeaderLabel>
					<Form onSubmit={handleSubmit(onSubmit)}>
						{mappedInputs}
						<ButtonContainer>
							<Button type="button" onClick={openPullEventsModal} variant={ButtonVariant.Quaternary}>
								<FilterButton>
									<div
										data-tooltip-id={MANUAL_EVENT_PULL_TOOLTIP_ID}
										data-tooltip-content="Manual Event Pull"
									>
										<GitIcon />
									</div>
								</FilterButton>
							</Button>
							<Tooltip
								id={MANUAL_EVENT_PULL_TOOLTIP_ID}
								place="bottom"
								style={{ zIndex: 9999, fontSize: "1.2rem", lineHeight: 1.35 }}
							/>
							<Button type="button" onClick={handleReset} variant={ButtonVariant.Quaternary}>
								<FilterButton>
									<ResetIcon />
								</FilterButton>
							</Button>
							{!isPoMapping && !isVenueAliases && <FiltersModal {...filtersProps} />}
							<Button
								style={{ width: "100px" }}
								type="submit"
								disabled={isSearchButtonDisabled}
								data-tooltip-id={shouldShowDateTooltip ? "headliner" : undefined}
								data-tooltip-content={shouldShowDateTooltip ? "Please select a date range in future to search." : undefined}>
								{shouldShowDateTooltip && <Tooltip id="headliner" place="bottom-end" style={{ zIndex: 9999 }} />}
								Search
							</Button>
						</ButtonContainer>
					</Form>
				</Container>
			</Main>
		</>
	);
};

export default Header;
